import { useCallback, useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { useMedia } from "@/contexts/MediaContext";
import { useMobile } from "@/hooks/useMobile";
import { FileInputArea } from "@/components/flow/shared/FileInputArea";
import { FileGridPreview } from "./FileGridPreview";
import { ObjectPromptCard } from "./ObjectPromptCard";
import { MediaItem } from "@/types/media";
import { UploadLimitHint } from "@/components/flow/build/Hints/UploadLimitHint";
import { useUploadAgain } from "@/hooks/useUploadAgain";

interface HomeFileInputAreaProps {
    addMediaItems: (files: File[]) => void;
    erroredMediaItems?: MediaItem[];
    qrSize?: number;
    showAllMethods?: boolean;
    onSubmit: (textPrompts: string) => void;
}

const containerAnimation = {
    initial: { opacity: 0, scale: 0.95 },
    animate: { opacity: 1, scale: 1 },
    exit: { opacity: 0, scale: 0.95 },
    transition: { duration: 0.3, ease: "easeOut" }
};

export const HomeFileInputArea = ({
    addMediaItems,
    erroredMediaItems = [],
    qrSize = 64,
    showAllMethods = false,
    onSubmit
}: HomeFileInputAreaProps) => {
    const { mediaItems, orderedMediaItems, uploadLimitError, clearUploadLimitError } = useMedia();
    const [isHovered, setIsHovered] = useState(false);
    const [hasEverUploadedFiles, setHasEverUploadedFiles] = useState(false);
    const isMobile = useMobile();
    const hasFiles = mediaItems.length > 0;

    // Track if files have ever been uploaded
    useEffect(() => {
        if (hasFiles && !hasEverUploadedFiles) {
            setHasEverUploadedFiles(true);
        }
    }, [hasFiles, hasEverUploadedFiles]);

    const handleAddFilesInPreview = useCallback(
        async (files: File[]) => {
            await addMediaItems(files);
        },
        [addMediaItems]
    );

    // Hook for "Upload Again" button in the floating hint
    const { fileInputRef, handleUploadAgain, handleFileSelected } = useUploadAgain({
        addMediaItems: handleAddFilesInPreview,
        clearUploadLimitError
    });

    // On mobile without files, don't use fixed height so tutorial card can shrink
    const disableDueToMobile = isMobile && !showAllMethods;

    return (
        <AnimatePresence mode="wait">
            <div
                className={`transparentContainer relative items-stretch ${disableDueToMobile ? "" : "h-96"}`}
            >
                {/* Hidden file input for "Upload Again" button */}
                <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*,video/*"
                    multiple
                    onChange={handleFileSelected}
                    className="hidden"
                />
                {/* Floating upload limit error - shown when files exist (FileInputArea is hidden) */}
                {hasFiles && !isMobile && (
                    <div className="absolute left-1/2 top-4 z-10 -translate-x-1/2 whitespace-nowrap">
                        <UploadLimitHint
                            uploadLimitError={uploadLimitError}
                            onUploadAgain={handleUploadAgain}
                            onDismiss={clearUploadLimitError}
                        />
                    </div>
                )}
                <motion.div
                    key="file-section"
                    initial={false}
                    animate={{ flexGrow: hasEverUploadedFiles && !isMobile ? 1 : 2, flexShrink: 0 }}
                    transition={{ duration: 0.3, ease: "easeOut" }}
                    className={`flex min-w-0 flex-1 overflow-hidden rounded-xl bg-white ${disableDueToMobile ? "" : "h-full"} ${hasFiles && !isMobile ? "border border-gray-300" : ""}`}
                    onMouseEnter={() => setIsHovered(true)}
                    onMouseLeave={() => setIsHovered(false)}
                >
                    {hasFiles && !isMobile ? (
                        <motion.div
                            key="file-grid-preview"
                            {...containerAnimation}
                            className="h-full w-full min-w-0"
                        >
                            <FileGridPreview
                                onAddFiles={handleAddFilesInPreview}
                                orderedMediaItems={orderedMediaItems}
                                isHomePage={true}
                            />
                        </motion.div>
                    ) : (
                        <motion.div
                            key="file-input-area"
                            {...containerAnimation}
                            className={`w-full min-w-0 ${disableDueToMobile ? "" : "h-full"}`}
                        >
                            <FileInputArea
                                addMediaItems={addMediaItems}
                                erroredMediaItems={erroredMediaItems}
                                qrSize={qrSize}
                                showAllMethods={showAllMethods}
                                externalIsHovered={isHovered}
                                hasEverUploadedFiles={hasEverUploadedFiles}
                            />
                        </motion.div>
                    )}
                </motion.div>
                {!isMobile && hasEverUploadedFiles && (
                    <motion.div
                        key="object-prompt-card"
                        initial={{ opacity: 0, flexGrow: 0, width: 0 }}
                        animate={{ opacity: 1, flexGrow: 1, width: "auto" }}
                        exit={{ opacity: 0, flexGrow: 0, width: 0 }}
                        transition={{ duration: 0.3, ease: "easeOut" }}
                        className="flex-1 overflow-hidden"
                    >
                        <ObjectPromptCard onSubmit={onSubmit} />
                    </motion.div>
                )}
            </div>
        </AnimatePresence>
    );
};
